import React from 'react';

import { Lock, RefreshCw, Tag } from 'lucide-react';

import { Badge } from '@codes/ui-kit';

export interface SettingItem {
    name: string;
    description?: string;
    type?: string;
    defaultValue?: unknown;
    options?: string[];
    isRequired?: boolean;
    isHidden?: boolean;
    immutable?: boolean;
    redeploy?: boolean;
    scope?: string;
}

export const SectionHeader = ({ icon: Icon, title }: { icon: React.ElementType; title: string }) => (
    <div className="flex items-center gap-2.5 mb-5">
        <div className="w-8 h-8 rounded-lg bg-point/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-point" />
        </div>
        <h2 className="text-lg font-semibold text-title tracking-tight">{title}</h2>
    </div>
);

export const SettingCard = ({ setting }: { setting: SettingItem }) => (
    <div className="group rounded-xl border border-detail-line bg-card p-4 transition-all duration-200 hover:border-point/30 hover:shadow-sm">
        <div className="flex items-start justify-between gap-3 mb-2">
            <code className="text-xs font-semibold text-title bg-box px-2 py-0.5 rounded-md">{setting.name}</code>
            <div className="flex items-center gap-1.5 shrink-0">
                {setting.isRequired && (
                    <Badge className="text-[10px] h-5 px-1.5 bg-error/10 text-error border-error/20 hover:bg-error/10">
                        required
                    </Badge>
                )}
                {setting.immutable && <Lock className="w-3 h-3 text-placeholder" />}
                {setting.redeploy && <RefreshCw className="w-3 h-3 text-link" />}
            </div>
        </div>
        <p className="text-xs text-sub-title leading-relaxed mb-2.5">{setting.description}</p>
        <div className="flex items-center gap-3 text-xs text-placeholder">
            <span className="flex items-center gap-1">
                <Tag className="w-3 h-3" />
                {setting.type}
            </span>
            {setting.defaultValue != null && (
                <span className="font-mono bg-box px-1.5 py-0.5 rounded text-sub">{String(setting.defaultValue)}</span>
            )}
            {setting.options && <span className="flex items-center gap-1">{setting.options.join(' | ')}</span>}
        </div>
    </div>
);

export const SettingsSection = ({
    settings,
    scope,
    icon: Icon,
}: {
    settings: SettingItem[];
    scope: string;
    icon: React.ElementType;
}) => {
    const filtered = settings.filter(s => s.scope === scope && !s.isHidden);
    if (filtered.length === 0) {
        return null;
    }

    return (
        <div className="mb-8 last:mb-0">
            <div className="flex items-center gap-2 mb-4">
                <Icon className="w-4 h-4 text-placeholder" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-placeholder">{scope}</h3>
                <div className="flex-1 h-px bg-divider" />
                <span className="text-xs text-placeholder">{filtered.length}</span>
            </div>
            <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
                {filtered.map(setting => (
                    <SettingCard key={setting.name} setting={setting} />
                ))}
            </div>
        </div>
    );
};
